
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch, Prop } from 'vue-property-decorator'

export interface PasswordPolicy {
  requiredLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireDigit: boolean
  requireNonAlphanumeric: boolean
}

@Component
export default class PasswordValidator extends Vue {
  @Prop({
    default: () => ({
      requiredLength: 12,
      requireUppercase: true,
      requireLowercase: true,
      requireDigit: true,
      requireNonAlphanumeric: true
    })
  })
  readonly policy!: PasswordPolicy

  @Prop({ default: false })
  readonly embedded!: boolean

  @Prop({ default: false })
  readonly hideBackButton!: boolean

  newPassword = ''
  confirmPassword = ''
  show = false

  loading = false
  error: string | null = null

  checks = {
    minLength: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false
  }

  isValid = false

  get passwordsMatch() {
    return (
      this.newPassword.length > 0 && this.newPassword === this.confirmPassword
    )
  }

  get canSubmit() {
    return this.isValid && this.passwordsMatch
  }

  public _ChangePassword() {
    this.onSubmit()
  }

  get strengthScore(): number {
    const pw = this.newPassword
    if (!pw) return 0

    let score = 0

    score += Math.min(40, pw.length * 4)

    if (/[A-Z]/.test(pw)) score += 15
    if (/[a-z]/.test(pw)) score += 15
    if (/[0-9]/.test(pw)) score += 15
    if (/[^a-zA-Z0-9]/.test(pw)) score += 15

    const unique = new Set(pw.split('')).size
    score += Math.min(20, unique * 2)

    return Math.max(0, Math.min(100, score))
  }

  get strengthLabel(): string {
    const s = this.strengthScore
    if (s < 40) return 'Weak'
    if (s < 70) return 'Medium'
    return 'Strong'
  }

  get strengthVariant(): string {
    const s = this.strengthScore
    if (s < 40) return 'danger'
    if (s < 70) return 'warning'
    return 'success'
  }

  @Watch('newPassword')
  onPwChanged(v: string) {
    this.recompute(v)
  }

  mounted() {
    this.recompute(this.newPassword)
  }

  private recompute(pw: string) {
    const p = this.policy

    const minLength = pw.length >= p.requiredLength
    const uppercase = /[A-Z]/.test(pw)
    const lowercase = /[a-z]/.test(pw)
    const digit = /[0-9]/.test(pw)
    const special = /[^a-zA-Z0-9]/.test(pw)

    this.checks = {
      minLength,
      uppercase: p.requireUppercase ? uppercase : true,
      lowercase: p.requireLowercase ? lowercase : true,
      digit: p.requireDigit ? digit : true,
      special: p.requireNonAlphanumeric ? special : true
    }

    this.isValid =
      this.checks.minLength &&
      this.checks.uppercase &&
      this.checks.lowercase &&
      this.checks.digit &&
      this.checks.special
  }

  async onSubmit() {
    this.error = null

    if (!this.canSubmit) return

    try {
      this.loading = true
      this.$emit('submit', { newPassword: this.newPassword })
    } catch (e) {
      this.error = 'Unexpected error.'
    } finally {
      this.loading = false
    }
  }
}
